import { motion } from 'framer-motion';
import { Activity, Lock, Moon, Sun } from 'lucide-react';
import { Navigate, Outlet } from 'react-router-dom';
import neolabShieldMinimal from '@/assets/images/neolab_shield_minimal.svg';
import { useAuthStore } from '@/store';
import { useThemeStore } from '@/store/themeStore';

export function AuthLayout() {
  const { isAuthenticated } = useAuthStore();
  const { resolvedTheme, setTheme } = useThemeStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white dark:bg-neolab-deep-green transition-colors duration-500">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-brand">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,_var(--tw-gradient-stops))] from-white/30 via-transparent to-transparent opacity-40" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,_var(--tw-gradient-stops))] from-black/20 via-transparent to-transparent opacity-60" />

          <motion.div
            animate={{
              y: [0, -30, 0],
              x: [0, 20, 0],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-20 left-20 w-80 h-80 bg-white rounded-full blur-[100px]"
          />
          <motion.div
            animate={{
              y: [0, 40, 0],
              x: [0, -30, 0],
              opacity: [0.1, 0.4, 0.1],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute bottom-40 right-20 w-[30rem] h-[30rem] bg-neolab-forest rounded-full blur-[120px]"
          />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.05, 0.1, 0.05] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-accent-orange rounded-full blur-[150px]"
          />
        </div>

        <div className="relative z-10 flex flex-col justify-center items-center w-full p-8 text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="max-w-md text-center"
          >
            <div className="mb-8">
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                className="inline-flex items-center justify-center w-24 h-24 rounded-2xl bg-white p-5 mb-6 shadow-2xl border-2 border-white/20"
              >
                <img
                  src={neolabShieldMinimal}
                  alt="Neolab Shield"
                  className="w-full h-full object-contain"
                />
              </motion.div>
              <h1 className="text-6xl font-black tracking-tighter mb-3 drop-shadow-2xl">
                NEO<span className="text-white/80">lab</span>
              </h1>
              <div className="h-1.5 w-28 bg-white/40 mx-auto rounded-full mb-6 overflow-hidden">
                <motion.div
                  animate={{ x: [-112, 112] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  className="w-full h-full bg-white shadow-[0_0_15px_rgba(255,255,255,0.8)]"
                />
              </div>
              <p className="text-white font-black text-xs uppercase tracking-[0.4em] opacity-90 drop-shadow-md">
                Medical Diagnostic Excellence
              </p>
            </div>

            <div className="space-y-6">
              <h2 className="text-4xl font-black leading-tight drop-shadow-lg">
                Precision <br />
                <span className="text-white/80 italic">Driven</span> Care
              </h2>
              <p className="text-white/90 text-base leading-relaxed font-medium">
                The next generation of laboratory workforce management, inspired by speed and
                reliability.
              </p>
            </div>

            <div className="mt-12 flex items-center justify-center gap-4">
              <div className="px-6 py-3 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 flex items-center gap-3 hover:bg-white/20 transition-all cursor-pointer">
                <Activity className="w-5 h-5 text-accent-orange" />
                <span className="font-black text-[10px] uppercase tracking-widest">
                  Real-time Sync
                </span>
              </div>
              <div className="px-6 py-3 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 flex items-center gap-3 hover:bg-white/20 transition-all cursor-pointer">
                <Lock className="w-5 h-5 text-accent-rose" />
                <span className="font-black text-[10px] uppercase tracking-widest">
                  Enterprise Security
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="flex-1 flex flex-col relative bg-white dark:bg-neolab-deep-green overflow-hidden">
        <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-neolab-forest/10 rounded-full blur-[150px] -mr-40 -mt-40 hidden dark:block" />
        <div className="absolute bottom-0 left-0 w-[30rem] h-[30rem] bg-accent-orange/5 rounded-full blur-[120px] -ml-20 -mb-20 hidden dark:block" />

        <div className="absolute top-6 right-6 z-20">
          <button
            type="button"
            onClick={toggleTheme}
            className="p-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-2 border-neolab-green/10 dark:border-white/10 shadow-sm hover:border-neolab-green transition-all group backdrop-blur-xl"
          >
            {resolvedTheme === 'dark' ? (
              <Sun className="w-5 h-5 text-amber-400 group-hover:rotate-90 transition-transform duration-500" />
            ) : (
              <Moon className="w-5 h-5 text-neolab-green group-hover:-rotate-12 transition-transform duration-500" />
            )}
          </button>
        </div>

        <div className="lg:hidden pt-12 pb-6 px-8 flex flex-col items-center">
          <div className="w-16 h-16 rounded-xl bg-gradient-brand flex items-center justify-center mb-4 shadow-2xl shadow-green-500/30 p-2">
            <img
              src={neolabShieldMinimal}
              alt="Neolab Shield"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-4xl font-black tracking-tighter dark:text-white">
            NEO<span className="text-neolab-green">lab</span>
          </h1>
        </div>

        <main className="flex-1 flex items-center justify-center p-6 lg:p-16 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-md"
          >
            <Outlet />
          </motion.div>
        </main>

        <footer className="p-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-neolab-green/5 dark:bg-white/5 border border-neolab-green/10 dark:border-white/10 mb-4 backdrop-blur-xl">
            <div className="w-1.5 h-1.5 rounded-full bg-neolab-green animate-pulse shadow-[0_0_10px_rgba(0,204,0,0.5)]" />
            <span className="text-[10px] font-black text-slate-400 dark:text-white/40 uppercase tracking-[0.3em]">
              System Protected & Operational
            </span>
          </div>
          <p className="text-[10px] text-slate-400 dark:text-white/30 font-black uppercase tracking-[0.2em] max-w-[200px] mx-auto">
            &copy; {new Date().getFullYear()} Neolab Medical Services
          </p>
        </footer>
      </div>
    </div>
  );
}

export default AuthLayout;
